/**
 * AI subscription costs + company costEntries ledger helpers.
 * Subscriptions are FIXED recurring costs — never mixed into usage budgets.
 */
import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { utcMonthKey } from './aiUsageSpend';

export const COST_SOURCE_AI_SUBSCRIPTION = 'ai_subscription';
export const COST_SOURCE_MANUAL = 'manual';

export const COST_CATEGORY_AI_SUBSCRIPTION = 'ai_subscription';
export const COST_CATEGORY_SHIFTYPM_PLAN = 'shiftypm_plan';
export const COST_CATEGORY_OPS = 'ops';

export const AI_SUBSCRIPTION_PROVIDERS = [
  { id: 'google', label: 'Google / Gemini' },
  { id: 'anthropic', label: 'Anthropic / Claude' },
  { id: 'deepseek', label: 'DeepSeek' },
  { id: 'openai', label: 'OpenAI' },
  { id: 'other', label: 'Other' },
];

/**
 * @param {unknown} value
 * @returns {string}
 */
function trimStr(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * @param {unknown} value
 * @returns {number | null}
 */
export function normalizeMonthlyCostUsd(value) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100) / 100;
}

/**
 * @returns {string}
 */
export function newSubscriptionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sub_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {unknown} raw
 * @returns {Array<object>}
 */
export function normalizeAiSubscriptions(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const id = trimStr(row.id) || newSubscriptionId();
      const monthlyCostUsd = normalizeMonthlyCostUsd(row.monthlyCostUsd);
      if (monthlyCostUsd == null) return null;
      const provider = trimStr(row.provider) || 'other';
      const planName = trimStr(row.planName) || 'Untitled plan';
      const billingDay =
        row.billingDayOfMonth == null || row.billingDayOfMonth === ''
          ? null
          : Number(row.billingDayOfMonth);
      return {
        id,
        provider,
        planName: planName.slice(0, 120),
        monthlyCostUsd,
        currency: 'USD',
        billingDayOfMonth:
          Number.isFinite(billingDay) && billingDay >= 1 && billingDay <= 28
            ? Math.floor(billingDay)
            : null,
        active: row.active !== false,
        notes: trimStr(row.notes).slice(0, 500) || null,
        updatedAt: row.updatedAt || null,
      };
    })
    .filter(Boolean);
}

/**
 * @param {Array<object>} subscriptions
 * @returns {number}
 */
export function sumActiveSubscriptionUsd(subscriptions) {
  return normalizeAiSubscriptions(subscriptions)
    .filter((s) => s.active)
    .reduce((sum, s) => sum + s.monthlyCostUsd, 0);
}

/**
 * Stable costEntries doc id for a subscription month row.
 * @param {string} subscriptionId
 * @param {string} periodMonth
 */
export function subscriptionCostEntryId(subscriptionId, periodMonth) {
  return `ai_sub_${trimStr(subscriptionId)}_${trimStr(periodMonth)}`;
}

/**
 * Sync active AI subscriptions into companies/{id}/costEntries.
 *
 * Mid-month edits must NOT rewrite an existing row for the current UTC month —
 * that month's amount is a point-in-time snapshot written on first sync.
 * Future months (and first-time current-month creates) may be upserted.
 *
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} companyId
 * @param {Array<object>} subscriptions
 * @param {{ uid?: string | null, monthsAhead?: number }} [opts]
 */
export async function syncSubscriptionCostEntries(
  db,
  companyId,
  subscriptions,
  opts = {},
) {
  const cid = trimStr(companyId);
  if (!cid) throw new Error('companyId is required.');
  const monthsAhead = Number.isFinite(opts.monthsAhead) ? opts.monthsAhead : 1;
  const now = new Date();
  const currentMonth = utcMonthKey(now);
  const months = [currentMonth];
  for (let i = 1; i <= monthsAhead; i += 1) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + i, 1));
    months.push(utcMonthKey(d));
  }

  const active = normalizeAiSubscriptions(subscriptions).filter((s) => s.active);
  const col = collection(db, 'companies', cid, 'costEntries');

  for (const sub of active) {
    for (const periodMonth of months) {
      const entryId = subscriptionCostEntryId(sub.id, periodMonth);
      const ref = doc(col, entryId);
      const existing = await getDoc(ref);

      // NO RETROACTIVE REWRITE: if a current-month row already exists, leave its
      // amountUsd (and related snapshot fields) unchanged when subscriptions are
      // edited mid-month. Future months and first-write current month still upsert.
      if (periodMonth === currentMonth && existing.exists()) {
        continue;
      }

      await setDoc(
        ref,
        {
          companyId: cid,
          category: COST_CATEGORY_AI_SUBSCRIPTION,
          provider: sub.provider,
          planName: sub.planName,
          amountUsd: sub.monthlyCostUsd,
          periodMonth,
          source: COST_SOURCE_AI_SUBSCRIPTION,
          subscriptionId: sub.id,
          updatedAt: serverTimestamp(),
          updatedByUid: opts.uid || null,
          ...(existing.exists()
            ? {}
            : {
                createdAt: serverTimestamp(),
                createdByUid: opts.uid || null,
              }),
        },
        { merge: true },
      );
    }
  }
}

/**
 * Write a manual cost ledger row (e.g. shiftypm_plan).
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} companyId
 * @param {{
 *   category: string,
 *   amountUsd: number,
 *   periodMonth?: string,
 *   provider?: string,
 *   planName?: string,
 *   notes?: string,
 *   uid?: string | null,
 * }} opts
 */
export async function writeManualCostEntry(db, companyId, opts) {
  const cid = trimStr(companyId);
  if (!cid) throw new Error('companyId is required.');
  const amountUsd = normalizeMonthlyCostUsd(opts.amountUsd);
  if (amountUsd == null) throw new Error('amountUsd must be a non-negative number.');
  const category = trimStr(opts.category) || COST_CATEGORY_OPS;
  const periodMonth = trimStr(opts.periodMonth) || utcMonthKey();
  const ref = doc(collection(db, 'companies', cid, 'costEntries'));
  await setDoc(ref, {
    companyId: cid,
    category,
    provider: trimStr(opts.provider) || null,
    planName: trimStr(opts.planName) || null,
    amountUsd,
    periodMonth,
    source: COST_SOURCE_MANUAL,
    notes: trimStr(opts.notes).slice(0, 500) || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdByUid: opts.uid || null,
    updatedByUid: opts.uid || null,
  });
  return ref.id;
}

/**
 * Sum costEntries for a period month (client-side from loaded docs).
 * @param {Array<object>} entries
 * @param {string} [periodMonth]
 */
export function sumCostEntriesUsd(entries, periodMonth) {
  const month = trimStr(periodMonth);
  return (Array.isArray(entries) ? entries : [])
    .filter((e) => !month || trimStr(e.periodMonth) === month)
    .reduce((sum, e) => {
      const n = normalizeMonthlyCostUsd(e.amountUsd);
      return sum + (n == null ? 0 : n);
    }, 0);
}
